import { useEffect, useRef, useState } from 'react'
import { Tag, Input } from 'antd'
import { TweenOneGroup } from 'rc-tween-one'
import { PlusOutlined } from '@ant-design/icons'

type Props = {
  label?: string
  onChange(tags: string[]): void
  yupSchema?: any
  onInputError?(error: string): void
}

function EditableTagGroup(props: Props) {
  const { label, onChange, yupSchema, onInputError } = props
  const [tags, setTags] = useState<string[]>([])
  const [inputVisible, setInputVisible] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState<string>('')
  const [inputError, setInputError] = useState<string>('')
  const inputRef = useRef<any>(null)

  useEffect(() => {
    if (onInputError) onInputError(inputError)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputError])

  useEffect(() => {
    onChange(tags)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags])

  const handleClose = (removedTag: string) => {
    setTags(tags.filter((tag: string) => tag !== removedTag))
  }

  const showInput = () => {
    setInputVisible(true)
  }

  useEffect(() => {
    if (inputVisible) {
      inputRef.current?.focus()
    }
  }, [inputVisible])

  const handleInputChange = (e: any) => {
    setInputValue(e.target.value)
  }

  const handleInputConfirm = async () => {
    let isValid
    if (yupSchema) {
      try {
        isValid = await yupSchema.validate(inputValue)
      } catch (error) {
        setInputError(error.message)
      }
    } else {
      isValid = true
    }

    if (inputValue && tags.indexOf(inputValue) === -1 && isValid) {
      setTags([...tags, inputValue])
      setInputValue('')
    }

    if (!inputRef.current.props.value) {
      setInputVisible(false)
      setInputError('')
    }
  }

  const forMap = (tag: string) => {
    const tagElem = (
      <Tag
        closable
        onClose={(e) => {
          e.preventDefault()
          handleClose(tag)
        }}
        style={{ marginBottom: '4px' }}
      >
        {tag}
      </Tag>
    )
    return (
      <span key={tag} style={{ display: 'inline-block' }}>
        {tagElem}
      </span>
    )
  }

  const tagChild = tags.map(forMap)

  return (
    <>
      {inputVisible && (
        <Input
          ref={inputRef}
          type="text"
          size="small"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputConfirm}
          onPressEnter={handleInputConfirm}
        />
      )}
      {!inputVisible && (
        <Tag
          onClick={showInput}
          style={{ width: '100%', background: '#fff', borderStyle: 'dashed' }}
          className="site-tag-plus"
        >
          <PlusOutlined /> {label || 'Nova Tag'}
        </Tag>
      )}
      {!!tagChild.length && (
        <div style={{ marginTop: 16 }}>
          <TweenOneGroup
            enter={{
              scale: 0.8,
              opacity: 0,
              type: 'from',
              duration: 100
            }}
            leave={{ opacity: 0, width: 0, scale: 0, duration: 200 }}
            appear={false}
          >
            {tagChild}
          </TweenOneGroup>
        </div>
      )}
    </>
  )
}

export default EditableTagGroup
